/* Agent Desk — live workspace for a human retention agent. Replaces the
 * old read-only /copilot mailbox.
 *
 * Layout: header + 4-pane row (profile / canvas / chat / transcript)
 * with drag-resize handles between panes and a collapse-to-dock button on
 * each side pane. Canvas auto-fills whatever space the panes/docks leave.
 *
 * What's live today:
 *   • Profile rail loads + renders the customer for an active call.
 *   • Resize + dock + persistence (layout state in localStorage).
 *   • Page chrome (rail nav, route, redirect from /copilot).
 *
 * Scaffolded — wired up in upcoming phases:
 *   • Stage-aware hero card in the canvas
 *   • Bidirectional Ask Co-Pilot chat
 *   • Live transcript stream
 */

import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { AskCoPilot } from "./agent-desk/AskCoPilot";
import { LiveAdvice } from "./agent-desk/LiveAdvice";
import { useAgentIdentity } from "./agent-desk/identity";
import { IntentCard } from "./agent-desk/IntentCard";
import { NudgeComposer } from "./agent-desk/NudgeComposer";
import { OfferCard } from "./agent-desk/OfferCard";
import { OutcomeCard } from "./agent-desk/OutcomeCard";
import { ProfileRail } from "./agent-desk/ProfileRail";
import { SignInModal } from "./agent-desk/SignInModal";
import { Softphone } from "./agent-desk/Softphone";
import { Transcript } from "./agent-desk/Transcript";
import {
  useCallStage,
  type CallOrchState,
  type Stage,
} from "./agent-desk/useCallStage";
import { useCurrentCall, type CallStatus } from "./agent-desk/useCurrentCall";
import { useCustomerForCall } from "./agent-desk/useCustomerForCall";
import { AppShell } from "./AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ChevronLeftIcon, ChevronRightIcon } from "./components/icons";
import { PaneResizer } from "./components/PaneResizer";

// ─── Layout state ──────────────────────────────────────────────────────
type PaneId = "profile" | "chat" | "transcript";
type PaneSide = "left" | "right";

type PaneState = { width: number; visible: boolean };
type Layout = Record<PaneId, PaneState>;

const PANE_LABEL: Record<PaneId, string> = {
  profile:    "Profile",
  chat:       "Co-Pilot",
  transcript: "Transcript",
};

const PANE_SIDE: Record<PaneId, PaneSide> = {
  profile:    "left",
  chat:       "right",
  transcript: "right",
};

const PANE_MIN = 200;
const PANE_MAX = 480;

const DEFAULT_LAYOUT: Layout = {
  profile:    { width: 250, visible: true },
  chat:       { width: 290, visible: true },
  transcript: { width: 240, visible: true },
};

const LAYOUT_KEY = "agent-desk:layout";

function loadLayout(): Layout {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw) as Partial<Layout>;
    // Merge field-by-field so a corrupt or partial blob can't wipe defaults.
    return {
      profile:    { ...DEFAULT_LAYOUT.profile,    ...parsed.profile },
      chat:       { ...DEFAULT_LAYOUT.chat,       ...parsed.chat },
      transcript: { ...DEFAULT_LAYOUT.transcript, ...parsed.transcript },
    };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function clampWidth(w: number): number {
  return Math.max(PANE_MIN, Math.min(PANE_MAX, w));
}

function usePaneLayout() {
  const [layout, setLayout] = useState<Layout>(loadLayout);

  // Persist on every change. localStorage writes are cheap; no debounce
  // needed at human drag rates. React batches setState during mousemove
  // so we don't blow up the event budget.
  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
    } catch {
      /* private mode — layout still works for the session */
    }
  }, [layout]);

  const toggleVisible = useCallback((id: PaneId) => {
    setLayout((prev) => ({
      ...prev,
      [id]: { ...prev[id], visible: !prev[id].visible },
    }));
  }, []);

  // direction: +1 means dragging the resizer right grows this pane, -1
  // shrinks it. Profile (left of canvas) grows on right-drag; chat and
  // transcript (right of canvas) shrink on right-drag.
  const resizePane = useCallback((id: PaneId, deltaX: number) => {
    setLayout((prev) => {
      const direction = PANE_SIDE[id] === "left" ? 1 : -1;
      const next = clampWidth(prev[id].width + deltaX * direction);
      if (next === prev[id].width) return prev;
      return { ...prev, [id]: { ...prev[id], width: next } };
    });
  }, []);

  return { layout, toggleVisible, resizePane };
}

// ─── Page ──────────────────────────────────────────────────────────────
export function AgentDesk({ callId: callIdFromRoute }: { callId?: number } = {}) {
  const { layout, toggleVisible, resizePane } = usePaneLayout();
  // When the page is mounted at the bare `/agent-desk` path with no id,
  // auto-track the most relevant call: live during the conversation,
  // wrap-up for ~2 min after end so the agent can finish notes, idle
  // when nothing's happening or the agent has dismissed wrap-up. See
  // useCurrentCall for the state machine.
  const current = useCurrentCall();
  const callId = callIdFromRoute ?? current.id;
  const callStatus: CallStatus =
    callIdFromRoute !== undefined ? "active" : current.status;
  const orch = useCallStage(callId);
  const customerState = useCustomerForCall(callId);
  const customerId =
    customerState.kind === "ready" ? customerState.customer.id : undefined;

  const { identity, signIn, clear } = useAgentIdentity();
  const [showSignIn, setShowSignIn] = useState(false);

  // First-visit prompt: if no name on record, open the modal as soon as
  // the page mounts. Switch button below re-opens it.
  useEffect(() => {
    if (identity === null) setShowSignIn(true);
  }, [identity]);

  return (
    <AppShell>
      <div id="agent-desk" className="agent-desk">
        {/* Sink for the @telnyx/webrtc SDK's remote audio stream.
            useTelnyxClient sets `client.remoteElement = "agent-desk-
            remote-audio"`; the SDK pipes the remote MediaStream into
            this element on call answer. autoPlay is required (Chrome
            won't play media without user interaction otherwise — and
            the agent's Accept click counts as that interaction).
            Hidden visually since playback control is via the call
            buttons, not native HTMLMediaElement controls. */}
        <audio
          id="agent-desk-remote-audio"
          autoPlay
          playsInline
          style={{ display: "none" }}
        />
        {showSignIn && (
          <SignInModal
            initialAgentId={identity?.agentId ?? null}
            onSubmit={(agentId, name) => {
              signIn(agentId, name);
              setShowSignIn(false);
            }}
            // exactOptionalPropertyTypes forbids passing `undefined` to an
            // optional prop; conditional-spread keeps the cancel handler
            // unset when there's no signed-in identity to fall back to.
            {...(identity !== null
              ? { onCancel: () => setShowSignIn(false) }
              : {})}
          />
        )}
        <AgentDeskHeader
          callId={callId}
          callStatus={callStatus}
          customerState={customerState}
          fromNumber={current.fromNumber}
          durationSecs={current.durationSecs}
          endedAgoSecs={current.endedAgoSecs}
          onDismissWrapup={current.dismiss}
          agentName={identity?.name ?? null}
          onSwitchAgent={() => setShowSignIn(true)}
          onSignOut={() => {
            clear();
            setShowSignIn(true);
          }}
        />

        <div id="agent-desk-grid" className="agent-desk-grid">
          {/* Per-pane error boundaries — a crash in one pane (e.g. the
           *  transcript subscriber chokes on a malformed SSE row)
           *  shouldn't take down the canvas + softphone next to it. */}
          {/* ── Left: profile rail ── */}
          {layout.profile.visible ? (
            <>
              <ErrorBoundary label="Profile rail">
                <ProfileRail
                  width={layout.profile.width}
                  callId={callId}
                  state={customerState}
                  onCollapse={() => toggleVisible("profile")}
                />
              </ErrorBoundary>
              <PaneResizer
                id="profile"
                onResize={(d) => resizePane("profile", d)}
              />
            </>
          ) : (
            <DockStrip
              id="profile"
              label={PANE_LABEL.profile}
              side="left"
              onExpand={() => toggleVisible("profile")}
            />
          )}

          {/* ── Center: canvas (auto-fills) ── */}
          <ErrorBoundary label="Canvas">
            <HeroCanvas
              hasCall={callId !== undefined}
              callId={callId}
              callStatus={callStatus}
              orch={orch}
              currentMonthlyCents={
                customerState.kind === "ready"
                  ? customerState.customer.monthly_charge_cents
                  : null
              }
            />
          </ErrorBoundary>

          {/* ── Right: chat pane ── */}
          {layout.chat.visible ? (
            <>
              <PaneResizer
                id="chat"
                onResize={(d) => resizePane("chat", d)}
              />
              <ErrorBoundary label="Ask Co-Pilot">
                <AskCoPilotPane
                  width={layout.chat.width}
                  callId={callId}
                  customerId={customerId}
                  onCollapse={() => toggleVisible("chat")}
                />
              </ErrorBoundary>
            </>
          ) : (
            <DockStrip
              id="chat"
              label={PANE_LABEL.chat}
              side="right"
              onExpand={() => toggleVisible("chat")}
            />
          )}

          {/* ── Far right: transcript pane ── */}
          {layout.transcript.visible ? (
            <>
              <PaneResizer
                id="transcript"
                onResize={(d) => resizePane("transcript", d)}
              />
              <ErrorBoundary label="Transcript">
                <TranscriptPane
                  width={layout.transcript.width}
                  callId={callId}
                  onCollapse={() => toggleVisible("transcript")}
                />
              </ErrorBoundary>
            </>
          ) : (
            <DockStrip
              id="transcript"
              label={PANE_LABEL.transcript}
              side="right"
              onExpand={() => toggleVisible("transcript")}
            />
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ─── Header ────────────────────────────────────────────────────────────
// Slim status row above the 4-pane grid. Shows page identity, the
// current call's tri-state (idle / active / wrap-up), and the signed-in
// agent on the right.
//
// The status copy is derived from useCurrentCall + useCustomerForCall:
//   idle    "No active call"
//   active  "On call: <Customer>" (or phone for unknown caller) + duration
//   wrapup  "Wrap-up: <Customer>" + "ended Xs ago" + Done button
function AgentDeskHeader({
  callId,
  callStatus,
  customerState,
  fromNumber,
  durationSecs,
  endedAgoSecs,
  onDismissWrapup,
  agentName,
  onSwitchAgent,
  onSignOut,
}: {
  callId: number | undefined;
  callStatus: CallStatus;
  customerState: ReturnType<typeof useCustomerForCall>;
  fromNumber: string | null;
  durationSecs: number | null;
  endedAgoSecs: number | null;
  onDismissWrapup: () => void;
  agentName: string | null;
  onSwitchAgent: () => void;
  onSignOut: () => void;
}) {
  // Derive the human-readable caller label once — used by both active
  // and wrap-up. Falls back to the from_number for unknown callers, and
  // ultimately to the call id if nothing else is loaded yet.
  const callerLabel = (() => {
    if (customerState.kind === "ready") {
      const c = customerState.customer;
      const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
      return name || c.business_name || fmtPhoneShort(c.phone);
    }
    if (customerState.kind === "no-customer" && customerState.fromNumber) {
      return `${fmtPhoneShort(customerState.fromNumber)} · unknown caller`;
    }
    if (fromNumber) return `${fmtPhoneShort(fromNumber)} · unknown caller`;
    return callId !== undefined ? `Call #${callId}` : "—";
  })();

  return (
    <header id="agent-desk-header" className="agent-desk-header">
      <div className="agent-desk-header-left">
        <span className="agent-desk-eyebrow">Agent Desk</span>
        <span className="agent-desk-header-state">
          {callStatus === "idle" && (
            <span className="agent-desk-state-idle">No active call</span>
          )}
          {callStatus === "active" && (
            <span className="agent-desk-state-active">
              On call: <strong>{callerLabel}</strong>
              {durationSecs !== null && (
                <span className="agent-desk-state-meta">
                  {" · "}
                  {fmtDuration(durationSecs)}
                </span>
              )}
            </span>
          )}
          {callStatus === "wrapup" && (
            <span className="agent-desk-state-wrapup">
              Wrap-up: <strong>{callerLabel}</strong>
              {endedAgoSecs !== null && (
                <span className="agent-desk-state-meta">
                  {" · ended "}
                  {fmtAgo(endedAgoSecs)}
                  {" ago"}
                </span>
              )}
              <button
                type="button"
                className="agent-desk-state-dismiss"
                onClick={onDismissWrapup}
                title="Mark wrap-up as done — clears the page back to idle"
              >
                Done
              </button>
            </span>
          )}
        </span>
      </div>
      <div className="agent-desk-header-right">
        <Softphone />
        {agentName ? (
          <span
            id="agent-desk-header-agent"
            className="agent-desk-header-agent"
          >
            <span className="agent-desk-header-agent-label">Signed in as</span>
            <strong className="agent-desk-header-agent-name">{agentName}</strong>
            <button
              id="agent-desk-header-agent-switch"
              type="button"
              className="agent-desk-header-agent-action"
              onClick={onSwitchAgent}
              title="Sign in as someone else"
            >
              Switch
            </button>
            <button
              id="agent-desk-header-agent-signout"
              type="button"
              className="agent-desk-header-agent-action"
              onClick={onSignOut}
              title="Clear local identity"
            >
              Sign out
            </button>
          </span>
        ) : (
          <button
            id="agent-desk-header-agent-signin"
            type="button"
            className="agent-desk-header-agent-action"
            onClick={onSwitchAgent}
          >
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}

// Compact phone formatter for the header — drops trailing 4 digits to
// "1234" rather than spaced out, since header real estate is tight.
function fmtPhoneShort(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

function fmtDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

function fmtAgo(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

// ─── Pane shell ────────────────────────────────────────────────────────
// Wraps each side pane with a uniform header bar that holds a title and
// a collapse button. Single visual seam across the side panes.
function PaneShell({
  id,
  width,
  onCollapse,
  side,
  title,
  className,
  children,
}: {
  id: string;
  width: number;
  onCollapse: () => void;
  side: PaneSide;
  title: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <aside
      id={`agent-desk-${id}-pane`}
      className={`agent-desk-pane ${className}`}
      style={{ width: `${width}px`, flex: `0 0 ${width}px` }}
    >
      <div className="agent-desk-pane-header">
        <span className="agent-desk-pane-title">{title}</span>
        <button
          id={`agent-desk-${id}-collapse-btn`}
          type="button"
          className="agent-desk-pane-collapse"
          onClick={onCollapse}
          aria-label={`Collapse ${id} pane`}
          title={`Collapse ${id} pane`}
        >
          {side === "left" ? <ChevronLeftIcon size={14} /> : <ChevronRightIcon size={14} />}
        </button>
      </div>
      {children}
    </aside>
  );
}

// ─── Dock strip (shown when a pane is collapsed) ───────────────────────
function DockStrip({
  id,
  label,
  side,
  onExpand,
}: {
  id: PaneId;
  label: string;
  side: PaneSide;
  onExpand: () => void;
}) {
  return (
    <button
      id={`agent-desk-${id}-dock`}
      type="button"
      className={`agent-desk-dock agent-desk-dock-${side}`}
      onClick={onExpand}
      aria-label={`Show ${label} pane`}
      title={`Show ${label} pane`}
    >
      <span className="agent-desk-dock-arrow" aria-hidden>
        {side === "left" ? <ChevronRightIcon size={14} /> : <ChevronLeftIcon size={14} />}
      </span>
      <span className="agent-desk-dock-label">{label}</span>
    </button>
  );
}

// ─── Hero canvas (center) ──────────────────────────────────────────────
// Renders the call's current stage when a call is connected: a small
// pill-stepper at the top, plus the orchestrator's hero content. The
// stage trail and intent card are real — driven by the bot writing
// current_stage and detected_intent on lifecycle events.
function HeroCanvas({
  hasCall,
  callId,
  callStatus,
  orch,
  currentMonthlyCents,
}: {
  hasCall: boolean;
  callId: number | undefined;
  callStatus: CallStatus;
  orch: CallOrchState;
  currentMonthlyCents: number | null;
}) {
  return (
    <main id="agent-desk-canvas" className="agent-desk-pane agent-desk-canvas">
      {hasCall && orch.stage !== null && (
        <div id="agent-desk-canvas-status" className="agent-desk-canvas-status">
          <StageTrail current={orch.stage} />
        </div>
      )}

      <div id="agent-desk-canvas-body" className="agent-desk-canvas-body">
        {!hasCall && (
          <div className="agent-desk-canvas-empty">
            <h2>No active call</h2>
            <p>
              The agent's live workspace renders here when a retention call
              is connected.
            </p>
          </div>
        )}
        {/* Agent-driven calls don't get an orchestrator stage (the AI
         *  flow's `current_stage` only fires when the bot is doing the
         *  talking). The canvas would otherwise sit empty for the entire
         *  call — instead, surface the advisor's live coaching here as
         *  the dynamic hero, per the mockup's "ONE HERO AT A TIME"
         *  pattern. Same component covers wrap-up so the agent can
         *  review what was suggested. */}
        {hasCall && orch.stage === null && callStatus === "active" && (
          <LiveAdvice callId={callId} callStatus={callStatus} />
        )}
        {hasCall && orch.stage === null && callStatus === "wrapup" && (
          <LiveAdvice callId={callId} callStatus={callStatus} />
        )}
        {hasCall && orch.stage === null && callStatus === "idle" && (
          <div className="agent-desk-canvas-pending">
            <h2>Connecting…</h2>
            <p>
              Waiting for the bot to register the call. The stage trail
              and per-stage guidance appear once the call is live.
            </p>
          </div>
        )}
        {hasCall && orch.stage !== null && callId !== undefined && (
          <>
            {/* LiveAdvice owns the dynamic-content hero on AI calls
             *  the same way it does on listen-mode. The StageTrail
             *  pill-stepper above provides the "you are here" cue; the
             *  advisor's suggested-approach / intent-root-cause /
             *  competitor-battlecard / offer-presentation / coaching
             *  cards replace the older static StageCard. */}
            <LiveAdvice callId={callId} callStatus={callStatus} />
            {/* IntentCard kept for the override pencil — LiveAdvice's
             *  intent_root_cause card is read-only. OfferCard kept for
             *  the per-grant accept/reject lifecycle, which the
             *  advisor's offer_presentation hero doesn't model. */}
            {(orch.intent !== null || orch.stage !== "start") && (
              <IntentCard
                callId={callId}
                intent={orch.intent}
                confidence={orch.intentConfidence}
                overriddenAt={orch.intentOverriddenAt}
              />
            )}
            {(orch.stage === "offer" || orch.stage === "close") && (
              <OfferCard
                callId={callId}
                currentMonthlyCents={currentMonthlyCents}
              />
            )}
            {orch.stage !== "close" && (
              <NudgeComposer callId={callId} ended={orch.endedAt !== null} />
            )}
            {orch.stage === "close" && (
              <OutcomeCard callId={callId} outcome={orch.outcome} />
            )}
          </>
        )}
      </div>
    </main>
  );
}

// ─── Stage trail ───────────────────────────────────────────────────────
// Pill-stepper showing the call's progression. `current` drives the
// done/current/upcoming state of each pill. Real data — bot writes
// current_stage on lifecycle events.
type StageDef = { value: Stage; label: string };
const STAGES: StageDef[] = [
  { value: "start",     label: "START" },
  { value: "discovery", label: "DISCOVERY" },
  { value: "offer",     label: "OFFER" },
  { value: "close",     label: "CLOSE" },
];

function StageTrail({ current }: { current: Stage }) {
  const currentIdx = STAGES.findIndex((s) => s.value === current);
  return (
    <div id="agent-desk-stage-trail" className="agent-desk-stage-trail">
      {STAGES.map((s, i) => {
        const state =
          i < currentIdx ? "done" : i === currentIdx ? "current" : "upcoming";
        return (
          <span
            key={s.value}
            id={`agent-desk-stage-step-${s.value}`}
            className={`agent-desk-stage-step is-${state}`}
          >
            {s.label}
          </span>
        );
      })}
    </div>
  );
}

// ─── Ask Co-Pilot pane (right of canvas) ───────────────────────────────
// Real chat now: text in, LLM answers via /api/agent-desk/chat. Disabled
// until a customer is attached to the active call (the chat needs that
// context to be useful).
function AskCoPilotPane({
  width,
  callId,
  customerId,
  onCollapse,
}: {
  width: number;
  callId: number | undefined;
  customerId: number | undefined;
  onCollapse: () => void;
}) {
  return (
    <PaneShell
      id="chat"
      width={width}
      onCollapse={onCollapse}
      side="right"
      title="Co-Pilot"
      className="agent-desk-chat-pane"
    >
      <AskCoPilot callId={callId} customerId={customerId} />
    </PaneShell>
  );
}

// ─── Transcript pane (far right) ───────────────────────────────────────
// Live transcript stream — same SSE feed that drives the Inbound /
// Outbound transcript tabs. Bubble layout tuned for the narrow rail.
function TranscriptPane({
  width,
  callId,
  onCollapse,
}: {
  width: number;
  callId: number | undefined;
  onCollapse: () => void;
}) {
  return (
    <PaneShell
      id="transcript"
      width={width}
      onCollapse={onCollapse}
      side="right"
      title="Transcript"
      className="agent-desk-transcript-pane"
    >
      <Transcript callId={callId} />
    </PaneShell>
  );
}
